/**
 * Pretty display names for AI workflow tools
 * Used across the AI chat interface for user-friendly tool name display
 */
export const PRETTY_TOOL_NAMES: Record<string, string> = {
  // Analytics Data Tools
  getExperience: "Getting Experience Data",
  getCountryCodeAudienceData: "Fetching Audience Data by Country Code",
  getDeviceTypeAudienceData: "Fetching Audience Data by Device Type",
  getLandingPageFullPathAudienceData: "Fetching Audience Data by Landing Page",
  getSourceChannelAudienceData: "Fetching Audience Data by Source Channel",
  getSourceSiteAudienceData: "Fetching Audience Data by Source Site",
  getNewOrReturningAudienceData: "Fetching New vs Returning Audience Data",

  // Integration Tools
  getShopCollections: "Fetching Collections",
  getShopPages: "Fetching Pages",

  // UI Tools
  setNeeds: "Evaluating Message...",
  setAudience: "Setting Audience",
  setCommonAudience: "Setting Audience",
  setCustomAudience: "Setting Audience",
  setAdvancedAudience: "Setting Audience",
  setCurrencyTargeting: "Setting Currency Targeting",
  setLinkTargeting: "Setting Link Targeting",
  setExperienceDetails: "Setting Experience Details",
  setKeyMetrics: "Setting Key Metrics",
  setCustomMetric: "Setting Custom Metrics",
  setOnsiteInjections: "Setting Onsite Injections",
  setPageTargeting: "Setting Page Targeting",
  setProductTargeting: "Setting Product Targeting",
  setTemplateRedirects: "Setting Template Redirects",
  setThemeRedirects: "Setting Theme Redirects",
  setUrlRedirects: "Setting URL Redirects",
  setVariations: "Setting Test Groups",

  // Experience Tools
  create_experience: "Creating Experience",
  update_experience: "Updating Experience",
  get_experience: "Getting Experience Data",
  get_experience_export: "Exporting Experience Data",
  get_experience_metrics_config: "Getting Experience Metrics Config",
  get_experience_timeseries: "Fetching Experience Timeseries Data",
  get_experiences_list_full: "Listing Experiences",
  get_experiences_list_lite: "Listing Experiences",
  list_experiments: "Listing Experiments",
  search_experiences: "Searching Experiences",
  search_experiments: "Searching Experiments",
  experience_action: "Performing Experience Action",
  analyze_experience: "Analyzing Experience",
  analytics_agent: "Analyzing Data",
  get_variation_audience: "Fetching Variation Audience Data",
  get_variation_overview: "Fetching Variation Overview",
  get_variation_products: "Fetching Variation Products",
  get_variation_timeseries: "Fetching Variation Timeseries Data",
  handoffToExperienceCreation: "Handing Off to Experience Creation",
  setAnalyticsViewType: "Setting Analytics View",
  setCustomProviderSource: "Setting Custom Provider Source",
  fetchContext: "Gathering Context",
  setOffer: "Setting up the offer",

  // Product & Collection Tools
  list_collections: "Listing Collections",
  get_collection_products: "Fetching Collection Products",
  list_products: "Listing Products",
  get_products: "Fetching Products",
  get_product: "Fetching Product",
  get_product_metafield_coverage: "Checking Product Metafield Coverage",
  get_catalog_fields: "Fetching Catalog Fields",
  search_products: "Searching Products",
  list_pages: "Listing Pages",
  list_integrations: "Listing Integrations",
  list_offers: "Listing Offers",
  list_custom_events: "Listing Custom Events",
  list_shop_themes: "Listing Themes",

  // Checkout Block Tools
  list_checkout_blocks: "Listing Checkout Blocks",
  add_checkout_block: "Adding Checkout Block",
  update_checkout_block: "Updating Checkout Block",
  delete_checkout_block: "Deleting Checkout Block",

  // Price Test Tools
  get_price_test_products: "Fetching Price Test Products",
  prepare_price_test_products: "Preparing Price Test Products",
  prepare_seed_price_test_products: "Preparing Seed Price Test Products",
  commit_price_test_products: "Committing Price Test Products",

  // Organization & Shop Tools
  get_organization: "Getting Organization Data",
  getOrganizationConfiguration: "Getting Organization Configuration",
  get_shop_info: "Getting Shop Info",
  get_shopify_free_shipping_rates: "Fetching Shopify Free Shipping Rates",
  switch_organization: "Switching Organization",

  // Analytics & Reporting Tools
  generate_custom_graph: "Generating Custom Graph",
  generate_intelligems_graph: "Generating Graph",
  get_analytics_summary_data: "Fetching Analytics Summary",
  get_event_snapshot: "Fetching Event Snapshot",
  get_event_timeseries: "Fetching Event Timeseries Data",
  get_revenue_by_product: "Fetching Revenue by Product",
  get_revenue_by_product_timeseries: "Fetching Revenue by Product Over Time",
  get_sales_overview: "Fetching Sales Overview",
  get_sitewide_conversion_funnel: "Fetching Sitewide Conversion Funnel",
  get_sitewide_order_distribution: "Fetching Sitewide Order Distribution",
  get_sitewide_snapshot: "Fetching Sitewide Snapshot",
  get_sitewide_timeseries: "Fetching Sitewide Timeseries Data",
  run_shopifyql_query: "Running ShopifyQL Query",
  get_shopifyql_reference: "Getting ShopifyQL Reference",

  // Screenshot / HTML Tools
  getDesktopHtmlAndScreenshot: "Capturing Desktop Preview",
  getDesktopScreenshot: "Capturing Desktop Screenshot",
  getMobileHtmlAndScreenshot: "Capturing Mobile Preview",
  getMobileScreenshot: "Capturing Mobile Screenshot",

  // Docs & Policy Tools
  searchDocs: "Searching Docs",
  search_policies: "Searching Policies",
  push_docs_to_github: "Publishing Docs",

  // Agentic Logic Tools
  build_agentic_logic_rule_proposal: "Preparing Rule Proposal",
  build_agentic_logic_template_rule_proposal:
    "Preparing Template Rule Proposal",
  create_agentic_logic_rule: "Creating Rule",
  update_agentic_logic_rule: "Updating Rule",
  save_agentic_logic_rule_draft: "Saving Rule Logic",
  publish_agentic_logic_rule: "Publishing Rule",
  get_agentic_logic_publish_job: "Checking Publish Status",
  evaluate_agentic_logic_rule: "Test-Running Rule",
  evaluate_agentic_logic_rule_scenario: "Running a Cart",
  list_agentic_logic_rule_scenarios: "Listing Example Carts",
  agentic_logic_rule_lifecycle: "Updating Rule Status",
  get_agentic_logic_rule: "Getting Rule",
  get_agentic_logic_rule_draft: "Getting Rule Draft",
  list_agentic_logic_rules: "Listing Rules",
  list_agentic_logic_rule_versions: "Listing Rule Versions",
  list_agentic_logic_templates: "Listing Rule Templates",
  validate_agentic_logic_template: "Checking Rule Settings",
  create_agentic_logic_placement: "Creating Placement",
  list_agentic_logic_placements: "Listing Placements",
  create_agentic_logic_placement_assignment: "Assigning Rule to Placement",
  update_agentic_logic_placement_assignment: "Updating Placement Assignment",
  delete_agentic_logic_placement_assignment: "Removing Rule from Placement",
  list_agentic_logic_placement_assignments: "Listing Placement Assignments",
  list_agentic_logic_datasets: "Listing Rule Datasets",
  search_agentic_logic_datasets: "Searching Rule Datasets",
  get_agentic_logic_dataset: "Getting Rule Dataset",
  put_agentic_logic_dataset: "Saving Rule Dataset",
  delete_agentic_logic_dataset: "Deleting Rule Dataset",
  list_agentic_logic_sources: "Listing Rule Sources",
  search_agentic_logic_sources: "Searching Rule Sources",
  get_agentic_logic_source: "Getting Rule Source",
  put_agentic_logic_source: "Saving Rule Source",
  delete_agentic_logic_source: "Deleting Rule Source",
  update_agentic_logic_exclusions: "Updating Rule Exclusions",

  // Other Tools
  "request-user-input": "Generating a question for you...",

  // AI QA Browser Tools
  browserbase_session_create: "Opening Browser",
  browserbase_session_close: "Closing Browser",
  browserbase_navigate: "Navigating",
  browserbase_get_url: "Checking Current URL",
  browserbase_act: "Interacting with Page",
  browserbase_observe: "Inspecting Page",
  browserbase_extract: "Extracting Page Data",
  browserbase_execute_js: "Running Browser Script",
  browserbase_scroll: "Scrolling Page",
  browserbase_annotate: "Annotating Screenshot",
  browserbase_screenshot: "Capturing Screenshot",
  browserbase_set_viewport: "Setting Viewport",
  setQaStatus: "Updating Progress",
  uploadAnnotatedScreenshotEvidence: "Capturing Evidence",
  browser_snapshot: "Capturing Browser Snapshot",
  reportCheckResults: "Report Findings",
  searchKnowledgeBase: "Searching Docs",
  readKnowledgeDocument: "Reading Docs",
  Task: "Starting QA Agent",
  Agent: "Starting QA Agent",

  // Add more tool names here as needed
  // Example categories for future expansion:
  // - Data Processing Tools
  // - Integration Tools
  // - Reporting Tools
};

/**
 * Get a pretty display name for a tool, falling back to the tool name itself
 * @param toolName - The internal tool name
 * @returns A user-friendly display name
 */
export const getPrettyToolName = (toolName: string): string => {
  const normalizedToolName = normalizeToolName(toolName);

  return PRETTY_TOOL_NAMES[normalizedToolName] || toolName;
};

/**
 * Strips the `mcp__<server>__` namespace an MCP-exposed tool carries, so callers
 * keying off a tool name see the same name whichever transport delivered it.
 */
export const normalizeToolName = (toolName: string): string => {
  const parts = toolName.split("__");

  if (parts[0] === "mcp" && parts.length >= 3) {
    return parts.slice(2).join("__");
  }

  return toolName;
};
